import React from 'react';
import { get } from 'lodash';
import { WrappedComponentProps, injectIntl } from 'react-intl';

import ElementStaticText from './StaticText';
import ElementFieldText from './FieldText';
import ElementFieldCurrency from './FieldCurrency';
import ElementFieldLookup from './FieldLookup';
import ElementFieldDateTime from './FieldDateTime';
import ElementFieldSelect from './FieldSelect';
import ElementFieldRecordReference from './FieldRecordRef';
import ElementFieldAttachment from './FieldAttachment';
import ElementFieldRating from './FieldRating';
import ElementFieldCheckbox from './FieldCheckbox';
import ElementFieldNumber from './FieldNumber';
import ElementFieldFormula from './FieldFormula';
import ElementFieldCollaborator from './FieldCollaborator';

import { ISpace, ImageDisplayType, TableModeType, ITableConfig } from '../../../types/space';
import { ElementTypes } from '../../../types/element';
import { getSpaceId } from '../../../helpers';

interface IProps extends WrappedComponentProps {
  space: ISpace<ElementTypes>;
  recordId: string;
  isExpand?: boolean;
  printDepth?: number;
}

// TODO Optimization: We should only pass in fieldId, the rest should be left up to the parent container
const PlatformElement: React.FC<IProps> = ({
  space,
  recordId,
  printDepth,
  intl: { formatMessage },
}) => {
  switch (space.type) {
    case ElementTypes.StaticText:
      return <ElementStaticText content={get(space, 'config.content')} />;
    case ElementTypes.StaticAttachment:
      return (
        <ElementFieldAttachment.StaticAttachment imageContent={get(space, 'config.imageContent')} />
      );
    case ElementTypes.FieldText:
    case ElementTypes.FieldLongText:
    case ElementTypes.FieldPhone:
    case ElementTypes.FieldEmail:
    case ElementTypes.FieldAutoNumber:
      if (!space.fieldId) return null;

      return (
        <ElementFieldText.WithFieldId
          fieldId={space.fieldId}
          id={getSpaceId(space.id, printDepth)}
          recordId={recordId}
        />
      );
    case ElementTypes.FieldNumber:
      if (!space.fieldId) return null;

      return <ElementFieldNumber.WithFieldId fieldId={space.fieldId} recordId={recordId} />;
    case ElementTypes.FieldCurrency:
      if (!space.fieldId) return null;

      return <ElementFieldCurrency.WithFieldId fieldId={space.fieldId} recordId={recordId} />;
    case ElementTypes.FieldFormula:
    case ElementTypes.FieldRollup:
      if (!space.fieldId) return null;

      return <ElementFieldFormula.WithFieldId fieldId={space.fieldId} recordId={recordId} />;
    case ElementTypes.FieldDateTime:
      if (!space.fieldId) return null;

      return <ElementFieldDateTime.WithFieldId fieldId={space.fieldId} recordId={recordId} />;
    case ElementTypes.FieldStatus:
      if (!space.fieldId) return null;

      return <ElementFieldSelect.StatusWithFieldId fieldId={space.fieldId} recordId={recordId} />;
    case ElementTypes.FieldSelect:
      if (!space.fieldId) return null;

      return <ElementFieldSelect.SingleWithFieldId fieldId={space.fieldId} recordId={recordId} />;
    case ElementTypes.FieldMultiSelect:
      if (!space.fieldId) return null;

      return <ElementFieldSelect.MultiWithFieldId fieldId={space.fieldId} recordId={recordId} />;
    case ElementTypes.FieldRating:
      if (!space.fieldId) return null;

      return <ElementFieldRating.WithFieldId fieldId={space.fieldId} recordId={recordId} />;
    case ElementTypes.FieldCheckbox:
      if (!space.fieldId) return null;

      return <ElementFieldCheckbox.WithFieldId fieldId={space.fieldId} recordId={recordId} />;
    case ElementTypes.FieldMultiAttachment:
      if (!space.fieldId) return null;

      return (space as ISpace<ElementTypes.FieldMultiAttachment>).config.imageDisplay ===
        ImageDisplayType.First ? (
        <ElementFieldAttachment.SingleWithFieldId
          fieldId={space.fieldId}
          recordId={recordId}
          spaceId={space.id}
        />
      ) : (
        <ElementFieldAttachment.MultiWithFieldId
          fieldId={space.fieldId}
          recordId={recordId}
          spaceId={space.id}
        />
      );
    case ElementTypes.FieldSubtable:
    case ElementTypes.FieldRecordRef:
      if (!space.fieldId) return null;

      return (space as ISpace<ElementTypes.FieldRecordRef>).config.tableMode ===
        TableModeType.Table ? (
        <ElementFieldRecordReference.TableWithFieldId
          id={getSpaceId(space.id, printDepth)}
          recordId={recordId}
          fieldId={space.fieldId}
          config={space.config as ITableConfig}
          spaceId={space.id}
        />
      ) : (
        <ElementFieldRecordReference.ListWithFieldId
          id={getSpaceId(space.id, printDepth)}
          recordId={recordId}
          fieldId={space.fieldId}
          config={space.config as ITableConfig}
          spaceId={space.id}
        />
      );
    case ElementTypes.FieldLookup:
      if (!space.fieldId) return null;

      return (
        <ElementFieldLookup
          id={getSpaceId(space.id, printDepth)}
          fieldId={space.fieldId}
          recordId={recordId}
          config={space.config as ITableConfig}
          spaceId={space.id}
        />
      );
    case ElementTypes.FieldCollaborator:
      if (!space.fieldId) return null;

      return <ElementFieldCollaborator fieldId={space.fieldId} recordId={recordId} />;
    default:
      console.log('what is my space: ', space);
      return <ElementFieldText.Raw value={formatMessage({ id: 'space.error.unsupported' })} />;
  }
};

export default React.memo(injectIntl(PlatformElement));
